/** Typed operation graph ports (console) — mirrors hub operationGraphPorts for validation UX. */

export type PortDataKind = "tabular" | "content" | "references";

export type PortDirection = "in" | "out";

export type PortDefinition = {
  id: string;
  direction: PortDirection;
  dataKind: PortDataKind;
  label: string;
  required?: boolean;
  multi?: boolean;
};

export type OperationGraphNodeType =
  | "provider"
  | "plex_api"
  | "transform"
  | "reactive_visualization"
  | "tantable"
  | "kpi_card"
  | "ai_provider"
  | "persist"
  | "persistent_provider"
  | "watch"
  | "file_provider"
  // ADR-0172 — Atlas Provider (platform state as page content).
  | "platform_provider"
  // ADR-0106 — Atlas Models (supervised foundation).
  | "model_train"
  | "model_predict"
  | "model_evaluate"
  // Control family — the bookend pair of a bounded for-each loop.
  | "loop_start"
  | "loop_end";

export const NODE_PORT_SIGNATURES: Record<OperationGraphNodeType, PortDefinition[]> = {
  provider: [
    { id: "scope_in", direction: "in", dataKind: "tabular", label: "Scope", required: false, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular data" }
  ],
  // ADR-0085 Phase 2: Plex API provider — same port shape as `provider`.
  plex_api: [
    { id: "scope_in", direction: "in", dataKind: "tabular", label: "Scope", required: false, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular data" }
  ],
  ai_provider: [
    { id: "scope_in", direction: "in", dataKind: "tabular", label: "Scope", required: false, multi: false },
    { id: "data_in", direction: "in", dataKind: "tabular", label: "Data context", required: false, multi: false },
    { id: "context_in", direction: "in", dataKind: "content", label: "Content context", required: false, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Structured data" },
    { id: "content_out", direction: "out", dataKind: "content", label: "Content" },
    { id: "refs_out", direction: "out", dataKind: "references", label: "References" }
  ],
  transform: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Tabular input", required: true, multi: true },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular output" }
  ],
  // ADR-0056: Reactive Visualization — terminal node. Exactly one tabular input,
  // no declared output port. The `render_out` storage row (see RENDER_MODEL_PORT
  // below) is NOT a declared graph port — it's a storage key for the compiled
  // chart readable by batch-resolve / inspector / widgets.
  reactive_visualization: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Tabular input", required: true, multi: false }
  ],
  // ADR-0070: TanTable — terminal render-output node. Single tabular input.
  tantable: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Tabular input", required: true, multi: false }
  ],
  // ADR-0070: KPI Card — terminal render-output node. Single tabular input.
  kpi_card: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Tabular input", required: true, multi: false }
  ],
  // ADR-0102: persist's input topology is policy-driven — snapshot policy
  // uses `tabular_in_incremental` only; keyed policies use both
  // `tabular_in_initial` and `tabular_in_incremental`. Both input ports are
  // declared here (each `required: false`) so the signature accepts either
  // wiring; the canvas renderer filters the inactive port by policy and the
  // hub validator enforces the policy-correct shape. See
  // `derivePersistInputTopology` below.
  persist: [
    { id: "tabular_in_initial", direction: "in", dataKind: "tabular", label: "Initial load", required: false, multi: false },
    { id: "tabular_in_incremental", direction: "in", dataKind: "tabular", label: "Incremental", required: false, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular output" }
  ],
  persistent_provider: [
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular output" }
  ],
  // ADR-0042: Watch — terminal on the data path; dispatches side-effects
  // configured in `node.config.responses` (no separate response node type).
  watch: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Tabular input", required: true, multi: false }
  ],
  // ADR-0102: file_provider — sibling of provider/plex_api. Produces tabular
  // data from a user-uploaded file via the file_provider routes on the
  // operation. Zero input ports.
  file_provider: [
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular data" }
  ],
  // ADR-0172: platform_provider — sibling of provider/file_provider serving
  // Atlas's own platform state from the hub's entity-view catalog. Zero
  // input ports; typed params in config replace scope_in.
  platform_provider: [
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Tabular data" }
  ],
  // ADR-0106 — Model nodes. Binding to a Model is by id in config (the
  // persistent_provider convention), not an edge. model_train is a sink with a
  // content_out training report; model_predict appends predictions; model_evaluate
  // emits per-row predicted-vs-actual + a metrics report.
  model_train: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Training data", required: true, multi: false },
    { id: "content_out", direction: "out", dataKind: "content", label: "Training report" }
  ],
  model_predict: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Rows to score", required: true, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Scored rows" }
  ],
  model_evaluate: [
    { id: "tabular_in", direction: "in", dataKind: "tabular", label: "Labelled data", required: true, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Predicted vs actual" },
    { id: "content_out", direction: "out", dataKind: "content", label: "Evaluation report" }
  ],
  // Control family. `loop_start` hands ONE ROW AT A TIME to the steps between
  // the bookends; `loop_end` is the fan-in and emits once for the whole run.
  //
  // `loop_end.result_in` is `required: false` and made policy-correct by the
  // hub validator (the persist precedent): genuinely required when results are
  // combined, genuinely absent when the body writes its own output. A port
  // signature cannot express "required unless config says otherwise".
  //
  // Two OUTPUTS on the end step, and the split is the point: `tabular_out`
  // always means the rows the loop produced, `log_out` always means how the
  // run went — one row per item. A single port whose meaning flipped with a
  // config value would be unknowable without reading that config, and schema
  // propagates on connection.
  loop_start: [
    { id: "collection_in", direction: "in", dataKind: "tabular", label: "Items to loop over", required: true, multi: false },
    { id: "item_out", direction: "out", dataKind: "tabular", label: "Current item" }
  ],
  loop_end: [
    { id: "result_in", direction: "in", dataKind: "tabular", label: "Result for this item", required: false, multi: false },
    { id: "tabular_out", direction: "out", dataKind: "tabular", label: "Combined results" },
    { id: "log_out", direction: "out", dataKind: "tabular", label: "Run log" }
  ]
};

// ---------------------------------------------------------------------------
// ADR-0102 — Input-topology helper for the universal persist node
//
// Mirror of the hub's `derivePersistInputTopology` (services/hub/src/pdo/types.ts).
// Snapshot policy gets one port (`tabular_in_incremental`); keyed policies get
// two ports (`tabular_in_initial` + `tabular_in_incremental`). Canvas-render
// and inspector handle-filtering both read off this helper; the topology is
// never stored as a field on the spec.
// ---------------------------------------------------------------------------

export type PdoWriteMode = "replace_snapshot" | "append" | "merge" | "merge_history";

export type PersistInputPort = "tabular_in_initial" | "tabular_in_incremental";

export type PersistInputTopology = {
  ports: PersistInputPort[];
  label: "snapshot" | "keyed";
};

export function derivePersistInputTopology(writeMode: PdoWriteMode | string | undefined): PersistInputTopology {
  if (writeMode === "replace_snapshot" || writeMode === undefined) {
    return { ports: ["tabular_in_incremental"], label: "snapshot" };
  }
  if (writeMode === "append" || writeMode === "merge" || writeMode === "merge_history") {
    return { ports: ["tabular_in_initial", "tabular_in_incremental"], label: "keyed" };
  }
  // Unknown write_mode — default to snapshot so the canvas/validator don't
  // silently drop the (always-present) incremental port.
  return { ports: ["tabular_in_incremental"], label: "snapshot" };
}

// ADR-0154 §6.3 C4 — the schedulable set is NOT declared here any more. It is
// owned by the hub (`SCHEDULABLE_NODE_TYPES`, ops/operationGraphPorts.ts) and
// served on the node-creation-shapes payload; read it with
// `isSchedulableNodeType` from `lib/nodeCreationShapes.ts`. The copy that used
// to live here was missing `model_train`.

export const NODE_LABELS: Record<OperationGraphNodeType, string> = {
  provider: "SQL Provider",
  plex_api: "Plex API",
  transform: "Transform",
  ai_provider: "AI Provider",
  reactive_visualization: "Reactive Viz",
  tantable: "TanTable",
  kpi_card: "KPI Card",
  persist: "Persist",
  persistent_provider: "Persistent Provider",
  watch: "Watch",
  file_provider: "File Provider",
  platform_provider: "Atlas Provider",
  model_train: "Model Train",
  model_predict: "Model Predict",
  model_evaluate: "Model Evaluate",
  loop_start: "Loop Start",
  loop_end: "Loop End"
};

/** Canvas node outline — CSS vars from styles.css :root (UI_GUIDELINES §4.2). */
export const NODE_OUTLINE_COLORS: Record<OperationGraphNodeType, string> = {
  provider: "var(--op-node-provider-border)",
  // ADR-0085 Phase 2: Plex API shares the provider outline color.
  plex_api: "var(--op-node-provider-border)",
  transform: "var(--op-node-transform-border)",
  ai_provider: "var(--op-node-ai-provider-border)",
  // ADR-0056: reactive viz shares the viz-border color by default. A
  // differentiating icon/badge on the palette entry + inspector header
  // carries the visual distinction (spec §8.1). Phase 10 may add a
  // dedicated --op-node-reactive-viz-border if the shared color proves
  // confusing in practice. ADR-0070: tantable and kpi_card also share
  // the viz-border color as terminal render-output nodes.
  reactive_visualization: "var(--op-node-viz-border)",
  tantable: "var(--op-node-viz-border)",
  kpi_card: "var(--op-node-viz-border)",
  persist: "var(--op-node-persist-border)",
  persistent_provider: "var(--op-node-persistent-provider-border)",
  watch: "var(--op-node-watch-border)",
  // ADR-0102: file_provider shares the provider outline color — it's a
  // provider-family node, sibling of SQL/Plex providers.
  file_provider: "var(--op-node-provider-border)",
  // ADR-0172: Atlas Provider is provider-family too — shared outline color.
  platform_provider: "var(--op-node-provider-border)",
  // ADR-0106: the three model nodes share one ML outline hue.
  model_train: "var(--op-node-model-border)",
  model_predict: "var(--op-node-model-border)",
  model_evaluate: "var(--op-node-model-border)",
  // Control family shares one outline hue. The PAIR accent (below) is what
  // distinguishes one loop from another — a per-type colour could not, since
  // every loop start would look identical to every other.
  loop_start: "var(--op-node-control-border)",
  loop_end: "var(--op-node-control-border)"
};

/**
 * Per-pair accents for loops, assigned by pair order within the operation.
 *
 * Two loops in one operation are the case that makes this load-bearing: the
 * bookends are joined by a config reference rather than an edge, so nothing on
 * the canvas inherently says which end belongs to which start. With one loop
 * that is inferable from context; with two it is not.
 */
export const LOOP_PAIR_ACCENTS: readonly string[] = [
  "var(--op-loop-accent-1)",
  "var(--op-loop-accent-2)",
  "var(--op-loop-accent-3)",
  "var(--op-loop-accent-4)"
];

/** Deterministic accent for a pair index — wraps rather than running out. */
export function loopPairAccent(pairIndex: number): string {
  return LOOP_PAIR_ACCENTS[pairIndex % LOOP_PAIR_ACCENTS.length]!;
}

export function findPortDefinition(
  nodeType: OperationGraphNodeType,
  portId: string,
  direction: PortDirection
): PortDefinition | undefined {
  return NODE_PORT_SIGNATURES[nodeType]?.find((p) => p.id === portId && p.direction === direction);
}

/** Whether a node type declares the canonical `tabular_out` output port. */
export function declaresTabularOutput(nodeType: OperationGraphNodeType | string): boolean {
  return Boolean(
    NODE_PORT_SIGNATURES[nodeType as OperationGraphNodeType]?.some(
      (p) => p.direction === "out" && p.id === "tabular_out"
    )
  );
}

/**
 * ADR-0146: whether a node type's output shape can be schema-locked. Derived
 * from the port signatures — same rule the hub route enforces — so a new
 * tabular node type gets the lock control without touching this file.
 */
export function supportsSchemaLock(nodeType: OperationGraphNodeType | string): boolean {
  return declaresTabularOutput(nodeType);
}

/**
 * The node-output rows a downstream binder (the optimize model's step pickers)
 * may offer as "steps with output". The node-outputs listing is one row per
 * (node, port) — input snapshots (`tabular_in*`) and render caches
 * (`render_out`) carry schemas too, so filtering by schema alone lists a step
 * once per row and offers render-terminal steps whose `tabular_out` no reader
 * can resolve. Bindable = the node's own `tabular_out` row, on a type that
 * declares that port.
 */
export function bindableTabularOutputs<T extends { port_id: string; node_type: string }>(rows: T[]): T[] {
  return rows.filter((r) => r.port_id === "tabular_out" && declaresTabularOutput(r.node_type));
}

export function normalizeGraphPort(args: {
  port: string;
  nodeType: string;
  direction: PortDirection;
  edgeType: "data" | "scope";
}): string {
  let p = args.port.trim();
  const at = p.indexOf("@");
  if (at > 0) p = p.slice(0, at);

  const { nodeType, direction, edgeType } = args;

  const typed = new Set([
    "tabular_in",
    "tabular_out",
    "scope_in",
    "content_out",
    "refs_out",
    "data_in",
    "context_in",
    // ADR-0102: persist policy-driven input ports.
    "tabular_in_initial",
    "tabular_in_incremental"
  ]);
  if (typed.has(p)) {
    // ADR-0102 safety net: legacy `tabular_in` targeting a persist normalizes
    // to `tabular_in_incremental` (always present regardless of policy).
    if (p === "tabular_in" && nodeType === "persist" && direction === "in") {
      return "tabular_in_incremental";
    }
    return p;
  }

  if (p === "data.out") return "tabular_out";
  if (p === "data.in") {
    if (nodeType === "persist" && direction === "in") return "tabular_in_incremental";
    return "tabular_in";
  }
  if (p === "data") {
    if (direction === "out") return "tabular_out";
    if (edgeType === "scope" && (nodeType === "provider" || nodeType === "ai_provider")) return "scope_in";
    if (nodeType === "persist" && direction === "in") return "tabular_in_incremental";
    return "tabular_in";
  }

  if (p === "scope.out" || p === "scope") {
    if (direction === "out") {
      return "tabular_out";
    }
    if (nodeType === "provider" || nodeType === "ai_provider") return "scope_in";
    return "scope_in";
  }
  if (p === "scope.in") return "scope_in";

  return p;
}

export function outputDataKindForPort(portId: string): PortDataKind | null {
  if (portId === "tabular_out") return "tabular";
  if (portId === "content_out") return "content";
  if (portId === "refs_out") return "references";
  return null;
}

export function isOutputKindCompatibleWithInputPort(outputKind: PortDataKind, inputPortId: string): boolean {
  if (inputPortId === "scope_in") return outputKind === "tabular";
  if (inputPortId === "tabular_in") return outputKind === "tabular" || outputKind === "references";
  // ADR-0102: persist's two policy-driven input ports accept the same data
  // kinds as the legacy `tabular_in` port.
  if (inputPortId === "tabular_in_initial" || inputPortId === "tabular_in_incremental") {
    return outputKind === "tabular" || outputKind === "references";
  }
  if (inputPortId === "context_in") return outputKind === "content";
  if (inputPortId === "data_in") return outputKind === "tabular";
  return false;
}

export function deriveEdgeTypeFromTargetPort(normalizedToPort: string): "data" | "scope" {
  return normalizedToPort === "scope_in" ? "scope" : "data";
}

export function stripGraphPortHandle(port: string): string {
  const at = port.indexOf("@");
  return at > 0 ? port.slice(0, at) : port;
}

export function inferEdgeTypeFromConnection(args: {
  sourceNodeType: OperationGraphNodeType;
  targetNodeType: OperationGraphNodeType;
  sourceHandle: string;
  targetHandle: string;
}): "data" | "scope" | null {
  const sh = stripGraphPortHandle(args.sourceHandle);
  const th = stripGraphPortHandle(args.targetHandle);

  const fromDef = findPortDefinition(args.sourceNodeType, sh, "out");
  const toDef = findPortDefinition(args.targetNodeType, th, "in");
  if (!fromDef || !toDef) return null;

  const outK = outputDataKindForPort(sh);
  if (!outK || !isOutputKindCompatibleWithInputPort(outK, th)) return null;

  if (th === "scope_in") return "scope";
  // ADR-0102: persist's policy-driven input ports also yield "data" edges.
  if (
    th === "tabular_in" ||
    th === "tabular_in_initial" ||
    th === "tabular_in_incremental" ||
    th === "data_in" ||
    th === "context_in"
  ) return "data";
  return null;
}

export function isPortsConnectionValid(args: {
  sourceNodeType: OperationGraphNodeType;
  targetNodeType: OperationGraphNodeType;
  sourceHandle: string;
  targetHandle: string;
}): boolean {
  return inferEdgeTypeFromConnection(args) !== null;
}

/**
 * ADR-0056 §5.3 — mirror of the hub's RENDER_MODEL_PORT constant.
 *
 * Maps each node type that produces a render_model to the `port_id` of the
 * `operation_node_outputs` row that stores it. Used by any consumer that
 * needs to read the "current chart" for a node — batch-resolve payload
 * handling, inspector render-model loaders, widget render pipeline.
 *
 * Route the `port_id` lookup through this map rather than hardcoding
 * strings — that way new node types with render models are one-line
 * additions here rather than grep-and-replace work.
 *
 *   "reactive_visualization" → "render_out" (ADR-0056)
 *   "tantable" → "render_out" (ADR-0070)
 *   "kpi_card" → "render_out" (ADR-0070)
 *
 * Callers that want a safe default when a type is missing should use
 * `RENDER_MODEL_PORT[nodeType] ?? "tabular_out"`.
 */
export const RENDER_MODEL_PORT: Partial<Record<OperationGraphNodeType, string>> = {
  reactive_visualization: "render_out",
  tantable: "render_out",
  kpi_card: "render_out"
};

/** Whether an AI Provider node has pipeline context ports wired (data / content). */
export function aiProviderContextInputStatus(args: {
  edges: Array<{
    source: string;
    target: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
    data?: { edge_type?: string } | null;
  }>;
  aiNodeId: string;
}): { dataIn: boolean; contextIn: boolean } {
  let dataIn = false;
  let contextIn = false;
  for (const e of args.edges) {
    if (e.target !== args.aiNodeId) continue;
    if (e.data?.edge_type !== "data") continue;
    const th = stripGraphPortHandle(String(e.targetHandle ?? ""));
    const normTo = normalizeGraphPort({
      port: th,
      nodeType: "ai_provider",
      direction: "in",
      edgeType: "data"
    });
    if (normTo === "data_in") dataIn = true;
    if (normTo === "context_in") contextIn = true;
  }
  return { dataIn, contextIn };
}
